import { useSetAtom } from 'jotai';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { patchProducts } from '@/api/public';
import { COMMON_TOAST_ERROR } from '@/const/errorMessage';
import { ADDRESS2, ADDRESS3 } from '@/const/products';
import { useToastContext } from '@/contexts/Common/ToastContext';
import { initialLicenseDraft, licenseDraftAtom, productsFormAtom, useResetProductsForm } from '@/store/products';

interface usePatchProductsMutationProps {
  url?: string;
  isFinalStep?: boolean;
  /** patch 성공 HTTP이나 응답 본문이 비어 있을 때 (예: 등록 완료 플로우에서 버튼 잠금 해제) */
  onInvalidResponse?: () => void;
}

const keepEnumDescIfMissing = (
  prev: ProductDetailResponse,
  next: ProductDetailResponse,
  key: 'loaded' | 'loadedDetail',
): ProductDetailResponse => {
  const prevVal = prev?.[key] as EnumPresenter | undefined;
  const nextVal = next?.[key] as EnumPresenter | undefined;

  if (!nextVal) return next;
  const isDescMissing = nextVal.desc == null || String(nextVal.desc).trim() === '';
  const isSameCode = prevVal?.code && nextVal.code && String(prevVal.code) === String(nextVal.code);

  if (isDescMissing && isSameCode && prevVal?.desc) {
    return {
      ...next,
      [key]: { ...nextVal, desc: prevVal.desc },
    } as ProductDetailResponse;
  }

  return next;
};

const isOneTonRangeTons = (tons: unknown) => {
  const n = Number(tons);
  return n === 1 || n === 1.1 || n === 1.2;
};

const keepLoadedIfMissingWithinOneTonRange = (prev: ProductDetailResponse, next: ProductDetailResponse) => {
  const prevInRange = isOneTonRangeTons(prev?.tons);
  const nextInRange = isOneTonRangeTons(next?.tons);
  if (!prevInRange || !nextInRange) return next;

  const nextLoadedCode = next?.loaded?.code;
  const isNextLoadedMissing = next?.loaded == null || nextLoadedCode == null || String(nextLoadedCode).trim() === '';

  if (isNextLoadedMissing && prev?.loaded?.code) {
    return {
      ...next,
      loaded: prev.loaded,
      // loaded가 유지될 때 세부 적재함도 함께 유지 (응답이 비어있으면)
      loadedDetail: next.loadedDetail ?? prev.loadedDetail,
    } as ProductDetailResponse;
  }

  return next;
};

const useUpdateProductFormMutation = ({ url, isFinalStep, onInvalidResponse }: usePatchProductsMutationProps) => {
  const navigate = useNavigate();
  const { showToast } = useToastContext();

  const setProductFormData = useSetAtom(productsFormAtom);
  const setLicenseDraft = useSetAtom(licenseDraftAtom);
  const resetProductsForm = useResetProductsForm();

  const { mutate, isLoading } = useMutation(patchProducts, {
    onSuccess: (response) => {
      if (response && response.data) {
        if (isFinalStep) {
          setLicenseDraft(initialLicenseDraft);
        }
        if (url) {
          navigate(url, { state: { isRegisterBtn: isFinalStep }, replace: true });
        }
        // 백엔드 응답 데이터를 검증하고 수정
        const responseData = response.data;

        const responseTons = responseData?.tons;
        const isResponseTonsOne = responseTons != null ? String(responseTons).trim() === '1' : false;

        if (!isResponseTonsOne) {
          responseData.area1 = '';
          responseData.area2 = '';
          responseData.area3 = '';
        }

        setProductFormData((prev) => {
          const merged0 = keepLoadedIfMissingWithinOneTonRange(prev, responseData);
          const merged1 = keepEnumDescIfMissing(prev, merged0, 'loaded');
          const merged2 = keepEnumDescIfMissing(prev, merged1, 'loadedDetail');
          return merged2;
        });
      } else {
        showToast('응답 데이터가 올바르지 않습니다.', 'error', 'bottom');
        onInvalidResponse?.();
      }
    },
    onError: () => {
      showToast(COMMON_TOAST_ERROR, 'error', 'bottom');
    },
  });

  return { mutate, isLoading };
};

export default useUpdateProductFormMutation;
